import type { Theme } from '@mui/material/styles';
import base from './base';
import palette from './palette';

const sharedIconButtonStates = {
  '&:hover': {
    backgroundColor: palette.primary.light,
  },
  '&:active': {
    backgroundColor: palette.primary.dark,
  },
  '&:focus-visible': {
    backgroundColor: palette.primary.light,
    borderColor: palette.primary.dark,
    color: palette.common.white,
    outline: '1px solid ' + palette.primary.dark,
    outlineOffset: '-2px',
  },
  '&:disabled': {
    backgroundColor: palette.grey[100],
    borderColor: palette.grey[600],
    color: palette.text.secondary,
  },
};

const button = {
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        fontSize: '1rem',
        textTransform: 'none',
      },
      contained: {
        border: '1px solid transparent',
        padding: '5px 15px',
      },
      containedPrimary: {
        '&:hover': {
          backgroundColor: palette.primary.light,
        },
        '&:active': {
          backgroundColor: palette.primary.dark,
        },
        '&:focus-visible': {
          backgroundColor: palette.primary.light,
          borderColor: palette.primary.dark,
          outline: `1px solid ${palette.primary.dark}`,
          outlineOffset: '-2px',
        },
        '&:disabled': {
          backgroundColor: palette.grey[100],
          borderColor: palette.grey[600],
          color: palette.text.secondary,
        },
      },
      outlined: ({ theme }: { theme: Theme }) => ({
        transition: theme.transitions.create(
          ['background-color', 'border-color', 'color'],
          { duration: theme.transitions.duration.short },
        ),
      }),
      outlinedPrimary: {
        borderColor: palette.primary.main,
        '&:hover': {
          backgroundColor: palette.primary.light,
          borderColor: palette.primary.light,
          color: palette.common.white,
        },
        '&:active': {
          backgroundColor: palette.primary.dark,
          color: palette.common.white,
        },
        '&:focus-visible': {
          backgroundColor: palette.primary.light,
          borderColor: palette.primary.dark,
          color: palette.common.white,
          outline: `1px solid ${palette.primary.dark}`,
          outlineOffset: '-2px',
        },
        '&:disabled': {
          backgroundColor: palette.grey[100],
          borderColor: palette.grey[600],
          color: palette.text.secondary,
        },
      },
      outlinedSecondary: {
        borderColor: palette.grey[600],
        color: palette.grey[800],
        '&:hover, &:active': {
          backgroundColor: palette.grey[100],
        },
        '&:focus-visible': {
          outline: '1px solid currentColor',
          outlineOffset: '-2px',
        },
      },
      text: {
        lineHeight: 1.2,
        borderRadius: base.shape.borderRadius,
        padding: '2px',
        '&:hover': {
          backgroundColor: 'transparent',
          color: palette.primary.light,
          textDecoration: 'underline',
        },
        '&:active': {
          color: palette.primary.dark,
        },
        '&:focus-visible': {
          backgroundColor: palette.red[100],
          outline: '1px dashed' + palette.primary.main,
          outlineOffset: 0,
        },
        '&:disabled': {
          color: palette.grey[700],
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: base.shape.borderRadius,
        '.MuiSvgIcon-root': {
          transition: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        '&:hover': {
          backgroundColor: palette.grey[600],
          color: palette.common.white,
        },
        '&:active': {
          backgroundColor: palette.grey[800],
          color: palette.common.white,
        },
        '&:focus-visible': {
          backgroundColor: palette.grey[600],
          borderColor: palette.grey[800],
          color: palette.common.white,
          outline: '1px solid ' + palette.grey[800],
          outlineOffset: '-1px',
        },
        '&:disabled': {
          color: palette.grey[200],
        },
      },
      colorPrimary: ({ theme }: { theme: Theme }) => ({
        backgroundColor: palette.primary.main,
        border: '1px solid transparent',
        color: palette.common.white,
        transition: theme.transitions.create(
          ['background-color', 'border-color', 'color'],
          { duration: theme.transitions.duration.short },
        ),
        ...sharedIconButtonStates,
      }),
      colorSecondary: ({ theme }: { theme: Theme }) => ({
        backgroundColor: palette.common.white,
        color: palette.primary.main,
        border: '1px solid',
        borderColor: palette.grey[600],
        transition: theme.transitions.create(
          ['background-color', 'border-color', 'color'],
          { duration: theme.transitions.duration.short },
        ),
        ...sharedIconButtonStates,
        '&:hover, &:active': {
          borderColor: 'transparent',
        },
      }),
      colorViolet: {
        backgroundColor: palette.common.white,
        border: '1px solid',
        borderColor: palette.blue[900],
        color: palette.blue[900],
        '& .MuiSvgIcon-root': {
          color: palette.blue[900],
        },
        '&:hover, &:active': {
          backgroundColor: palette.blue[900],
          color: palette.common.white,
          '& .MuiSvgIcon-root': {
            color: palette.common.white,
          },
        },
        '&:focus-visible': {
          backgroundColor: palette.blue[100],
          borderColor: palette.blue[900],
          outline: '1px solid ' + palette.blue[900],
          outlineOffset: '-2px',
        },
      },
      sizeSmall: {
        padding: 2,
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        padding: 0,
        textTransform: 'none',
        border: 'none',
        color: palette.grey[800],
        '&.Mui-selected': {
          color: palette.common.white,
          backgroundColor: palette.red[600],
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: palette.red[600],
          },
        },
        '&:hover': {
          color: palette.common.white,
          backgroundColor: palette.red[600],
          borderRadius: '4px',
        },
      },
    },
    defaultProps: {
      size: 'small',
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        padding: '3px',
        height: '40px',
        border: '1px solid',
        borderColor: palette.grey[600],
        background: palette.grey[100],
        '&:has(.MuiToggleButton-root:not(.Mui-selected):hover) .Mui-selected': {
          color: palette.red[600],
          backgroundColor: 'transparent',
        },
      },
    },
  },
};

export default button;
